import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, LogIn, AlertCircle } from "lucide-react";
import { useI18n } from "@/i18n";
import api from "@/api";

export default function AdminLogin() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("gsm_token", data.access_token);
      localStorage.setItem("gsm_user", JSON.stringify(data.user));
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : t("admin.login.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-admin-login" className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="mx-auto w-14 h-14 rounded-full bg-[#B95736]/10 flex items-center justify-center text-[#B95736]">
            <Lock size={20} strokeWidth={1.5} />
          </div>
          <h1 className="mt-6 font-serif text-4xl tracking-tight text-stone-900">{t("admin.login.title")}</h1>
          <p className="mt-2 text-stone-600">{t("admin.login.subtitle")}</p>
        </div>

        <form onSubmit={submit} className="p-8 rounded-lg border border-stone-200 bg-white space-y-5" data-testid="admin-login-form">
          <label className="block">
            <span className="text-xs font-mono tracking-widest uppercase text-stone-600 block mb-2">{t("admin.login.email")}</span>
            <input
              data-testid="admin-login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-md focus:outline-none focus:border-[#B95736] focus:ring-2 focus:ring-[#B95736]/20 transition-colors"
            />
          </label>
          <label className="block">
            <span className="text-xs font-mono tracking-widest uppercase text-stone-600 block mb-2">{t("admin.login.password")}</span>
            <input
              data-testid="admin-login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-[#FAF9F6] border border-stone-200 rounded-md focus:outline-none focus:border-[#B95736] focus:ring-2 focus:ring-[#B95736]/20 transition-colors"
            />
          </label>

          {error && (
            <div data-testid="admin-login-error" className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-red-900 text-sm">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <div>{error}</div>
            </div>
          )}

          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="btn-primary w-full justify-center"
          >
            <LogIn size={16} />
            {loading ? "…" : t("admin.login.submit")}
          </button>
        </form>
      </div>
    </div>
  );
}
